package com.siriusxm.pia.views.mddb

import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.utils.prettyPrintXML
import com.siriusxm.pia.NotificationType
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import contentingestion.unifiedmodel.EntityType
import io.ktor.client.plugins.*
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Provides an additional tab to display raw MDDB information for an entity.
 */
open class MddbAugmenter(val api: MddbAPI) {
    val name: String = "MDDB"
    @Composable
    fun render(entityType: EntityType, entityId: String) {
        var enhancerData by remember { mutableStateOf<RawReadDetailsResponse?>(null) }
        var view by remember { mutableStateOf<String>("xml") }
        var error by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(entityId) {
            try {
                error = null
                enhancerData = lookupEntityAugmentation(entityType, entityId)
            } catch (e: ResponseException) {
                error = e.message
            }
        }

        if (error != null) {
            NotificationBox(
                Notification(
                    NotificationType.ERROR.name, "We were unable to load the data from MDDB",
                    error
                )
            )
        } else if (enhancerData != null) {
            val rawReadDetails = enhancerData
            if (rawReadDetails != null) {
                rawReadDetails.data?.forEach { rawResponse ->

                    val url = rawResponse.url
                    val xml = rawResponse.rawData

                    Div({
                        style {
                            textAlign("right")
                            margin(.3.em, 0.px)
                        }
                    }) {
                        buttonRadioGroup(view) {
                            option("Table", "table")
                            option("XML", "xml")
                            onChange {
                                view = it
                            }
                        }
                    }

                    Div {
                        if (view == "table") {
                            Style(JsonViewStyles)
                            var entity: JsonElement? = entityFromXml(xml.prettyPrintXML())

                            if (entity != null) {
                                entity = removeSingleItemElements(entity)

                                if (entity is JsonObject) {
                                    entity = JsonObject(entity.filter { it.key != "messages" })
                                }

                                entity = removeSingleItemElements(entity)

                                if (entity is JsonObject) {
                                    box({ paddedContent = false }) {
                                        DefaultJsonViewRenderer(true)
                                        jsonTable(entity, true)
                                    }
                                }
                            }
                        } else if (view == "xml") {
                            box({ paddedContent = false }) {
                                xmlTextView(xml.prettyPrintXML())
                            }
                        } else {
                            // nothing
                        }
                    }

                    detailGrid {
                        detail("URL") {
                            Span({ classes(JsonViewStyles.copyableContentContainer) }) {
                                Text(url)
                                copyContentIcon(url)
                            }
                        }
                    }
                }
            }
        } else {
            spinner(size = Size.NORMAL)
        }
    }

    suspend fun lookupEntityAugmentation(entityType: EntityType, entityId: String): RawReadDetailsResponse? {
        return api.fetchRawDetails(entityId)
    }
}

fun removeSingleItemElements(json: JsonElement): JsonElement {
    return if (json is JsonObject) {
        if (json.size == 1) {
            removeSingleItemElements(json.values.first())
        } else {
            json
        }
    } else {
        json
    }
}


/**
 * Renders an XML element as text
 */
@Composable
fun xmlTextView(data: String) {
    Div {
        Pre({
            style {
                backgroundColor(SXMUI.containerContentBackgroundColor.value())
                padding(1.em)
                whiteSpace("pre-wrap")
            }
        }) {
            Text(data)
        }
    }
}

fun entityFromXml(xml: String): JsonObject {
    val cleanedXml = xml.substringAfter("?>").trim() // Remove the XML header
    val stack = mutableListOf<Pair<String, MutableMap<String, JsonElement>>>()
    var currentMap = mutableMapOf<String, JsonElement>()

    val regex = Regex("<(/?)([a-zA-Z0-9]+)>([^<]*)")
    val matches = regex.findAll(cleanedXml)

    for (match in matches) {
        val isClosing = match.groupValues[1] == "/"
        val tagName = match.groupValues[2]
        val content = match.groupValues[3].trim()

        if (isClosing) {
            // Closing tag
            val (parentTag, parentMap) = stack.removeLast()
            if (currentMap.size == 1 && currentMap.containsKey(tagName)) {
                // Collapse single-element maps into primitives
                parentMap[parentTag] = currentMap[tagName] ?: JsonNull
            } else {
                parentMap[parentTag] = JsonObject(currentMap)
            }
            currentMap = parentMap
        } else {
            // Opening tag
            stack.add(tagName to currentMap)
            currentMap = mutableMapOf()
            if (content.isNotEmpty()) {
                currentMap[tagName] = JsonPrimitive(content)
            }
        }
    }

    return JsonObject(currentMap)
}
